import { useEffect, useState } from "react";
import { portalApi } from "@/lib/portalApi";
import { CalendarCheck, MapPin, Video, Phone } from "lucide-react";
import { toast } from "sonner";

const MODALITY_LABELS = {
  video: "Video visit",
  phone: "Phone call",
  in_person: "In-person",
  home_visit: "Home visit",
};

export default function PortalAppointments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalApi.get("/portal/appointments")
      .then(({ data }) => setItems(data || []))
      .catch(() => toast.error("Couldn't load your appointments."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-5" data-testid="portal-appointments-page">
      <div>
        <p className="text-xs uppercase tracking-wider text-[#7E937D] font-medium">Your visits</p>
        <h1 className="font-display text-3xl font-semibold text-[#0F172A] mt-1">Upcoming appointments</h1>
        <p className="text-sm text-[#334155] mt-2">
          Visits already scheduled with your HarborWell team. If you need to
          reschedule, reach out to your Care Navigator from the Home tab.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-10 text-center" data-testid="portal-appointments-empty">
          <CalendarCheck className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-[#0F172A] font-medium">No upcoming visits</p>
          <p className="text-sm text-[#334155] mt-2">When your Care Navigator schedules a visit with you, it'll show up here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <AppointmentCard key={a.id} a={a} />
          ))}
        </div>
      )}
    </div>
  );
}

function AppointmentCard({ a }) {
  const dt = a.start_at ? new Date(a.start_at) : null;
  const day = dt ? dt.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" }) : "";
  const time = dt ? dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "";
  const Icon = a.modality === "video" ? Video : a.modality === "phone" ? Phone : MapPin;
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-5" data-testid={`portal-appt-${a.id}`}>
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#DBEAFE] text-[#1E3A8A] flex items-center justify-center flex-shrink-0">
          <Icon className="w-6 h-6" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg font-semibold text-[#0F172A]">{a.title || "Visit"}</div>
          <div className="text-sm text-[#334155]">{day} · {time}</div>
          <div className="mt-2 text-xs text-[#1E3A8A]">{MODALITY_LABELS[a.modality] || a.modality || "Visit"}</div>
          {a.with_staff_name && (
            <div className="text-xs text-[#334155]">with {a.with_staff_name}</div>
          )}
          {a.location && (
            <div className="text-xs text-[#334155] mt-1">📍 {a.location}</div>
          )}
          {a.video_url && (
            <a
              href={a.video_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs font-medium"
              data-testid={`portal-appt-join-${a.id}`}
            >
              <Video className="w-3.5 h-3.5" /> Join video
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
